import { Component, forwardRef, Inject, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatError, MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { MessageDto, MessageManagementModel } from '@ay-gosu/server-shared';
import { AsyncJobModule } from '@ay-gosu/ui/common/async-job';
import { MatConnectedDialog } from '@ay-gosu/ui/common/connected-dialog';
import { Content } from '@ay/bot';
import Bluebird from 'bluebird';
import { firstValueFrom } from 'rxjs';
import { LegacyAppearanceDirective } from '../../material/legacy/mat-form-field/legacy-appearance.directive';
import { MatTooltip } from '../../material/tooltip/tooltip';
import { CardsMessageService } from '../../message/cards/cards-message.service';
import { MessageFactoryService } from '../../message/message-factory.service';
import { PackageFactoryService } from '../../message/package-factory.service';
import { Package } from '../../message/package.class';
import { PreviewComponent } from '../../message/preview/preview.component';
import { ToolbarComponent } from '../../message/toolbar/toolbar.component';
import { CreateFolderComponent } from '../../pages/message-management/create-folder/create-folder.component';
import { AccountPipe } from '../../pipe/account.pipe';
import { MomentPipe } from '../../pipe/moment.pipe';
import { MessageManagementService } from '../../service/message-management.service';
import { DialogPathService } from '../dialog-path.service';
import { ConfirmEditComponent } from './confirm-edit/confirm-edit.component';

@Component({
  selector: 'gosu-message-dialog',
  templateUrl: './message.dialog.html',
  styleUrls: ['./message.dialog.scss'],
  imports: [
    MomentPipe,
    AccountPipe,
    LegacyAppearanceDirective,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatButton,
    MatIcon,
    MatIconButton,
    MatSelect,
    MatOption,
    MatTooltip,
    MatError,
    ToolbarComponent,
    AsyncJobModule,
    forwardRef(() => PreviewComponent),
  ],
})
export class MessageDialog implements OnInit {
  public folders$ = this._messageManagementService.folderList$;

  public folderId = new FormControl(null, Validators.required);

  public name = new FormControl('未命名訊息', Validators.required);

  public formGroup = new FormGroup({
    name: this.name,
    folderId: this.folderId,
  });

  public package: Package;

  public message: MessageDto;

  public title: string;

  public loading = true;

  public constructor(
    private readonly _matDialogRef: MatDialogRef<any>,
    private readonly _messageManagementService: MessageManagementService,
    private readonly _matSnackBar: MatSnackBar,
    private readonly _packageFactoryService: PackageFactoryService,
    private readonly _messageFactoryService: MessageFactoryService,
    private readonly _matConnectedDialog: MatConnectedDialog,
    private readonly _router: Router,
    private readonly _cardsMessageService: CardsMessageService,
    private readonly _dialogPathService: DialogPathService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      type: 'message' | 'share-message' | 'share-add-friend';
      accountId: number;
      folderId: number;
      messageId?: number;
      isEdit?: boolean;
    },
  ) {}

  public async ngOnInit() {
    this.folderId.setValue(this.data.folderId);

    if (this.data.messageId) {
      const message = await this._messageManagementService.fetchMessageById(
        this.data.messageId,
      );
      if (!message) return;

      this.message = message;

      const raw = await MessageManagementModel.fetchPackage(message.id);
      this.name.setValue(message.name);
      this.package =
        await this._packageFactoryService.createForMessageManagement();
      this.package.id = raw.id;
      this.package.name = message.name;
      this.package.messages = await Bluebird.map(raw.records, (record) =>
        this._messageFactoryService.createFromContent(record),
      );
      this.package.checkError = () => {
        return this._messageManagementService.checkError(this.package);
      };
    }

    let title = '建立新訊息';

    switch (this.data.type) {
      case 'share-message':
        title = '建立分享訊息';
        this.package.toolbars = this.package.toolbars.filter(
          (toolbar) => toolbar.type !== 'poster',
        );
        this.package.enabledActions.message = false;
        this.package.enabledActions.next = false;
        this.package.enabledActions.postback = false;
        this.package.enabledActions.dateTimePicker = false;

        break;
      case 'share-add-friend':
        title = '建立分享加 LINE 好友訊息';

        if (!this.data.messageId) {
          this.package =
            await this._packageFactoryService.createForMessageManagement();
          this.package.messages = [
            await this._cardsMessageService.createForShareAddFriend(-1),
          ];
        }

        this.package.toolbars = this.package.toolbars.filter(
          (toolbar) => toolbar.type !== 'poster',
        );
        this.package.enabledActions.message = false;
        this.package.enabledActions.next = false;
        this.package.enabledActions.postback = false;
        this.package.enabledActions.dateTimePicker = false;
        this.package.enabledActions.addFriendThroughShare = false;

        break;
    }

    if (this.data.messageId) {
      title = '編輯訊息';
    }

    if (this.message?.isShared) {
      this.package.toolbars = this.package.toolbars.filter(
        (toolbar) => toolbar.type !== 'poster',
      );

      const res = await MessageManagementModel.fetchMessageUsed(
        this.data.messageId,
      );

      const onlyUsedBySelf =
        res.messageUsed.length === 1 &&
        res.messageUsed[0]?.messageId === this.data.messageId;

      if (!onlyUsedBySelf) {
        this.package.enabledActions.message = false;
        this.package.enabledActions.next = false;
        this.package.enabledActions.postback = false;
        this.package.enabledActions.dateTimePicker = false;
      }
    }

    if (this.data.isEdit) {
      this.package.toolbars = this.package.toolbars.filter(
        (toolbar) => toolbar.type !== 'poster',
      );
    }

    this._dialogPathService.push(title);
    this.title = this._dialogPathService.title;

    this._matDialogRef.afterClosed().subscribe(() => {
      this._dialogPathService.pop();
    });

    this.loading = false;
  }

  public getLastEditorAccountId() {
    return this.message?.accountId ?? this.data.accountId;
  }

  public getLastEditTime() {
    return this.message?.updatedAt ?? new Date();
  }

  public async createFolder() {
    const dialogRef = this._matConnectedDialog.open(CreateFolderComponent);
    const res = await firstValueFrom(dialogRef.afterClosed());

    if (res) {
      await this._router.navigateByUrl(`/message-management/${res.id}`);

      this.folders$.reload();
      await this.folders$.toPromise();
      this.folderId.setValue(res.id);
    }
  }

  public close() {
    this._matDialogRef.close();
  }

  public async save() {
    const records = this.package.messages.map((message) => message.toJSON());

    let res = null;
    if (this.data.messageId) {
      if (this.data.isEdit) {
        res = await this.saveEdit(records);
      } else {
        await MessageManagementModel.update({
          id: this.data.messageId,
          folderId: this.folderId.value,
          name: this.name.value,
          records,
        });
      }
    } else {
      res = await MessageManagementModel.create({
        folderId: this.folderId.value,
        name: this.name.value,
        records,
      });
    }

    this._matSnackBar.open($localize`儲存成功`);
    this._matDialogRef.close({
      folderId: this.folderId.value,
      messageId: res?.id ?? this.data.messageId,
      name: res?.name ?? this.name.value,
      packageId: res?.packageId ?? this.package.id,
    });
  }

  public async saveEdit(records: Content.Any[]) {
    const dialogRef = this._matConnectedDialog.open(ConfirmEditComponent);
    const dialog = await firstValueFrom(dialogRef.afterClosed());

    if (!dialog) return;

    let res = null;

    if (dialog.type === 'save') {
      await MessageManagementModel.update({
        id: this.data.messageId,
        folderId: this.folderId.value,
        name: this.name.value,
        records,
      });
    } else if (dialog.type === 'save-as-new') {
      records.forEach((record) => {
        delete record.recordId;
        delete record.packageId;

        if (record.type === 'cards') {
          record.cards.forEach((card) => {
            if (card.type === 'card') {
              delete card.cardId;
              card.buttons.forEach((button) => {
                delete button.buttonId;
                delete button.cardId;
                delete card.recordId;
                delete card.packageId;
              });
            }

            delete card.recordId;
            delete card.packageId;
          });
        }

        if (record.type === 'poster') {
          delete record.posterId;
          record.areas.forEach((area) => {
            delete area.areaId;
            delete area.posterId;
            delete area.recordId;
            delete area.packageId;
          });
        }
      });

      res = await MessageManagementModel.create({
        folderId: this.folderId.value,
        name: this.name.value + '_的副本',
        records,
      });
    }

    this._messageManagementService.folderList$.reload();

    return res;
  }
}
